import { useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const fmtCost = (c) => (c === null || c === undefined ? null : `$${c.toFixed(c < 0.01 ? 4 : 2)}`);
const fmtNum = (n) => (n || 0).toLocaleString();
const fmtWhen = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) + " " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

const Stat = ({ label, value, sub }) => (
  <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2.5">
    <p className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</p>
    <p className="mt-0.5 text-lg font-700 text-zinc-100">{value}</p>
    {sub && <p className="text-[11px] text-zinc-500">{sub}</p>}
  </div>
);

export const AiUsageMeter = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setData(await api.adminAiUsage()); } catch { setData(null); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="flex items-center gap-2 py-3 text-sm text-zinc-400" data-testid="ai-usage-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading usage…</div>;
  }
  if (!data) {
    return <p className="py-2 text-sm text-zinc-400" data-testid="ai-usage-error">Couldn't load AI usage right now.</p>;
  }

  const hasCost = data.has_cost;
  const monthValue = hasCost ? (fmtCost(data.month_cost) || "$0.00") : fmtNum(data.month_calls);

  return (
    <div className="space-y-4" data-testid="ai-usage-meter">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <Stat label="This month" value={monthValue} sub={hasCost ? `${fmtNum(data.month_calls)} AI reads` : "AI reads"} />
        <Stat label="All-time reads" value={fmtNum(data.total_calls)} sub={hasCost ? (fmtCost(data.total_cost) || "$0.00") + " total" : "screenshots + queries"} />
        <Stat
          label="Avg per tour sheet"
          value={(() => {
            const t = (data.by_feature || []).find((f) => f.feature === "tour_sheet");
            if (!t) return "—";
            return hasCost && t.avg_cost != null ? (fmtCost(t.avg_cost) || "—") : `${fmtNum(t.avg_tokens)} tok`;
          })()}
          sub={(() => {
            const t = (data.by_feature || []).find((f) => f.feature === "tour_sheet");
            return t ? `${fmtNum(t.avg_tokens)} tokens/read` : "no uploads yet";
          })()}
        />
      </div>

      {(data.by_feature || []).length > 0 && (
        <div className="overflow-hidden rounded-lg border border-zinc-800" data-testid="ai-usage-by-feature">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-900/60 text-left text-[11px] uppercase tracking-wide text-zinc-500">
                <th className="px-3 py-2 font-500">Feature</th>
                <th className="px-3 py-2 text-right font-500">Reads</th>
                <th className="px-3 py-2 text-right font-500">Avg tokens</th>
                <th className="px-3 py-2 text-right font-500">{hasCost ? "Total cost" : "Total tokens"}</th>
              </tr>
            </thead>
            <tbody>
              {data.by_feature.map((f) => (
                <tr key={f.feature} className="border-b border-zinc-800/60 last:border-0" data-testid={`ai-usage-row-${f.feature}`}>
                  <td className="px-3 py-2 text-zinc-200">{f.label}</td>
                  <td className="px-3 py-2 text-right text-zinc-300">{fmtNum(f.calls)}</td>
                  <td className="px-3 py-2 text-right text-zinc-300">{fmtNum(f.avg_tokens)}</td>
                  <td className="px-3 py-2 text-right text-zinc-100">{hasCost ? (f.cost != null ? fmtCost(f.cost) : "—") : fmtNum(f.total_tokens)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!hasCost && data.total_calls > 0 && (
        <p className="text-xs text-zinc-500" data-testid="ai-usage-cost-note">
          Token counts are exact. Your provider isn't returning a per-call dollar figure, so for the authoritative spend check Profile → Manage plan → Universal Key.
        </p>
      )}
      {data.total_calls === 0 && (
        <p className="text-sm text-zinc-400" data-testid="ai-usage-empty">No AI activity yet. Upload a tour sheet or ask Insights a question and it'll show up here.</p>
      )}

      {(data.recent || []).length > 0 && (
        <div>
          <p className="mb-1.5 text-[11px] uppercase tracking-wide text-zinc-500">Recent activity</p>
          <ul className="divide-y divide-zinc-800/60 overflow-hidden rounded-lg border border-zinc-800" data-testid="ai-usage-recent">
            {data.recent.map((r, i) => (
              <li key={i} className="flex items-center justify-between gap-3 bg-zinc-900/40 px-3 py-2 text-sm">
                <span className="min-w-0 truncate text-zinc-200">{r.label}</span>
                <span className="flex shrink-0 items-center gap-3 text-xs text-zinc-500">
                  <span>{fmtNum(r.total_tokens)} tok</span>
                  {hasCost && r.cost != null && <span className="text-zinc-400">{fmtCost(r.cost)}</span>}
                  <span>{fmtWhen(r.created_at)}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button onClick={load} className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-200" data-testid="ai-usage-refresh">
        <RefreshCw className="h-3.5 w-3.5" /> Refresh
      </button>
    </div>
  );
};
